// Week timeline for one person in the full report: a row per day, 24 hours across, with
// each shift drawn as a bar coloured by its most serious finding. Bars are SVG rects
// positioned by attributes, because the CSP (style-src 'self') blocks inline styles.
// The SVG is decorative; each day's shifts are also listed as text for screen readers.
import {
  DAY_MINUTES,
  type NightBand,
  type TimelineBar,
  type TimelineWeek,
} from '../../report/timeline';
import { SEVERITY_LABELS } from '../../report/labels';

const GRID = [6, 12, 18].map((h) => h * 60);
const AXIS = ['00:00', '06:00', '12:00', '18:00', '24:00'];

interface Props {
  weeks: TimelineWeek[];
  night: NightBand;
  /** Shifts named by the finding the user picked, drawn with an outline. */
  highlighted: ReadonlySet<string>;
  /** Clicking a bar picks the first finding about that shift. */
  onPickShift(shiftId: string): void;
}

export function Timeline({ weeks, night, highlighted, onPickShift }: Props) {
  const nightSpans: [number, number][] =
    night.from > night.to
      ? [
          [night.from, DAY_MINUTES],
          [0, night.to],
        ]
      : [[night.from, night.to]];
  return (
    <div class="timeline">
      {weeks.map((week) => (
        <section key={week.monday} class="timeline-week" aria-label={week.label}>
          <h4>{week.label}</h4>
          <ul class="timeline-days">
            {week.days.map((day) => (
              <li key={day.date} class="timeline-day">
                <span class="timeline-date">{day.label}</span>
                <svg
                  class="timeline-track"
                  viewBox={`0 0 ${DAY_MINUTES} 20`}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  {nightSpans.map(([from, to]) => (
                    <rect
                      key={from}
                      class="timeline-night"
                      x={from}
                      y={0}
                      width={to - from}
                      height={20}
                    />
                  ))}
                  {GRID.map((x) => (
                    <line key={x} class="timeline-grid" x1={x} x2={x} y1={0} y2={20} />
                  ))}
                  {day.bars.map((bar) => (
                    <rect
                      key={bar.shiftId}
                      class={barClass(bar, highlighted)}
                      x={bar.from}
                      y={3}
                      width={Math.max(bar.to - bar.from, 8)}
                      height={14}
                      vector-effect="non-scaling-stroke"
                      onClick={bar.severity ? () => onPickShift(bar.shiftId) : undefined}
                    >
                      <title>{barText(bar)}</title>
                    </rect>
                  ))}
                </svg>
                <span class="visually-hidden">
                  {day.bars.length === 0
                    ? 'No shifts.'
                    : day.bars.map((bar) => `${barText(bar)}.`).join(' ')}
                </span>
                {day.bars.length === 0 && (
                  <span class="timeline-off muted" aria-hidden="true">
                    Day off
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div class="timeline-axis" aria-hidden="true">
            <span />
            <span class="timeline-ticks">
              {AXIS.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </span>
          </div>
        </section>
      ))}
      <p class="timeline-legend muted">
        <span class="key key-breach" /> {SEVERITY_LABELS.breach} <span class="key key-risk" />{' '}
        {SEVERITY_LABELS.risk} <span class="key key-info" /> {SEVERITY_LABELS.info}{' '}
        <span class="key key-none" /> Nothing found · {night.label}
      </p>
    </div>
  );
}

function barClass(bar: TimelineBar, highlighted: ReadonlySet<string>): string {
  return [
    'timeline-bar',
    `bar-${bar.severity ?? 'none'}`,
    bar.severity ? 'clickable' : '',
    highlighted.has(bar.shiftId) ? 'highlighted' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

function barText(bar: TimelineBar): string {
  const brk =
    bar.breakMinutes === undefined
      ? ''
      : bar.breakMinutes === 0
        ? ', no break'
        : `, ${bar.breakMinutes}-minute break`;
  const status = bar.severity ? `, ${SEVERITY_LABELS[bar.severity]}` : '';
  return `Shift ${bar.label}${brk}${status}`;
}
